import { useState, useEffect, useCallback } from "react";
import { T } from "../theme";
import { apiCall } from "../apiClient";
import { fmtDate } from "../utils";
import { Card, Badge, SectionTitle, EmptyState, RefreshBtn } from "./primitives";
import { useClientScope } from "../contexts/ClientScopeContext";
import { formatDisplayNumber } from "../utils/displayNumber";

// MyDeskView — in-house edition only, lawyer-role only.
// Lists my open assignments (status NOT IN executed/cancelled) ordered
// oldest-untouched first, with a per-BU count breakdown sidebar.
//
// Visibility is enforced both backend-side (the /api/dashboard/my-desk
// endpoint returns 404 outside in-house edition or for non-lawyer roles)
// and frontend-side (the Sidebar nav item is hidden in firm edition or
// for non-lawyer users). The backend gate is authoritative; the frontend
// gate is just to avoid showing a button that 404s.

type DeskItem = {
  id: string;
  title: string;
  counterparty: string | null;
  request_type: string | null;
  status: string;
  priority: string | null;
  business_unit_id: string | null;
  business_unit_name: string | null;
  updated_at: string;
  created_at: string;
  target_date: string | null;
  display_number: number | null;
  client_display_code: string | null;
};

type MyDesk = {
  items: DeskItem[];
  by_bu: { [k: string]: { name: string; count: number } };
};

type Props = { onOpen?: (id: string) => void };

export const MyDeskView = ({ onOpen }: Props) => {
  const [data, setData] = useState<MyDesk | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const { edition: scopeEdition, tenantPrefix } = useClientScope();

  const load = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const resp = await apiCall(`/dashboard/my-desk`);
      if (resp && Array.isArray(resp.items)) {
        setData(resp);
        setLastRefresh(new Date());
      } else {
        setError("Unable to load My Desk");
      }
    } catch (e: any) {
      setError(e?.message || "Unable to load My Desk");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(true); }, [load]);
  useEffect(() => { const i = setInterval(() => load(false), 60000); return () => clearInterval(i); }, [load]);

  if (loading && !data) {
    return <div style={{ padding: 40, textAlign: "center", color: T.txM, fontSize: 12 }}>Loading My Desk...</div>;
  }
  if (error) {
    return <EmptyState icon="!" msg={error} />;
  }
  if (!data || data.items.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <SectionTitle sub="Your open assignments across all business units, oldest-untouched first.">My Desk</SectionTitle>
        <EmptyState icon="✎" msg="Nothing on your desk. New assignments will appear here." />
      </div>
    );
  }

  const buEntries = Object.entries(data.by_bu).sort((a, b) => b[1].count - a[1].count);
  const totalOpen = data.items.length;

  return (
    <div style={{ padding: 24 }}>
      <SectionTitle
        sub={`${totalOpen} open ${totalOpen === 1 ? "assignment" : "assignments"}, ordered oldest-untouched first.`}
        right={<RefreshBtn onClick={() => load(true)} />}
      >My Desk</SectionTitle>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 240px", gap: 20, marginTop: 16 }}>
        <div>
          {data.items.map((r) => {
            const ageMs = Date.now() - new Date(r.updated_at).getTime();
            const ageDays = Math.floor(ageMs / (1000 * 60 * 60 * 24));
            const displayNum = formatDisplayNumber(r as any, "request", { edition: scopeEdition, tenant_prefix: tenantPrefix });
            return (
              <Card
                key={r.id}
                onClick={onOpen ? () => onOpen(r.id) : undefined}
                style={{ marginBottom: 8, cursor: onOpen ? "pointer" : "default", borderLeft: `3px solid ${ageDays >= 14 ? T.red : ageDays >= 7 ? T.orn : T.brd}` }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      {displayNum && <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 11, color: T.txM, padding: "2px 6px", backgroundColor: T.bg2, borderRadius: 4, border: `1px solid ${T.brd}` }}>{displayNum}</span>}
                      <span style={{ fontSize: 14, fontWeight: 600, color: T.tx }}>{r.title}</span>
                      {r.priority && r.priority !== "Normal" && (
                        <Badge c={r.priority === "Urgent" ? T.red : T.orn} b={r.priority === "Urgent" ? T.redS : T.ornS}>
                          {r.priority}
                        </Badge>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: T.txM, marginBottom: 4 }}>
                      {r.counterparty || "—"} · {r.request_type || "—"} · {r.business_unit_name || "No BU"}
                    </div>
                    <div style={{ fontSize: 11, color: T.txD }}>
                      Last touched {fmtDate(r.updated_at)} · {ageDays === 0 ? "today" : `${ageDays}d ago`}
                    </div>
                  </div>
                  <Badge c={T.txM} b={T.bg4} size="md" dot>
                    {r.status.replace(/_/g, " ")}
                  </Badge>
                </div>
              </Card>
            );
          })}
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.txM, marginBottom: 8, letterSpacing: 0.5, textTransform: "uppercase" }}>By Business Unit</div>
          {buEntries.map(([k, v]) => (
            <div key={k} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderBottom: `1px solid ${T.brd}`, fontSize: 13 }}>
              <span style={{ color: T.tx }}>{v.name}</span>
              <span style={{ color: T.txM, fontWeight: 600 }}>{v.count}</span>
            </div>
          ))}
          {lastRefresh && (
            <div style={{ fontSize: 11, color: T.txD, marginTop: 12, textAlign: "right" }}>
              Refreshed {fmtDate(lastRefresh)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
